<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import ChatAPI from 'custom/api/chat';
import { useLocoI18n } from 'custom/composables/useLocoI18n';
import { CHAT_EVENTS, useChat } from './useChat';
import { useAgents } from './useAgents';
import { dayKey, dayLabel, groupsWithPrevious } from './format';
import ChatMessage from './ChatMessage.vue';
import ChatComposer from './ChatComposer.vue';
import ThreadPanel from './ThreadPanel.vue';
import PeoplePicker from './PeoplePicker.vue';

const { t, locale } = useLocoI18n();
const route = useRoute();
const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');
const currentRole = useMapGetter('getCurrentRole');
const agents = useAgents();
const chat = useChat({ currentUserId: currentUser.value?.id });

const channel = ref(null);
const messages = ref([]);
const hasMore = ref(false);
const isLoadingOlder = ref(false);
const firstUnreadId = ref(null);
const threadId = ref(null);
const listRef = ref(null);
const composerRef = ref(null);
const membersDialogRef = ref(null);
const newMembers = ref([]);

const channelId = computed(() => Number(route.params.channelId));
const currentUserId = computed(() => currentUser.value?.id);
const browserLocale = computed(() => locale.value.replace('_', '-'));
const canManage = computed(
  () => channel.value?.role === 'owner' || currentRole.value === 'administrator'
);
const typingNames = computed(() => chat.typingNames(channelId.value));
const memberIds = computed(() =>
  (channel.value?.members || []).map(member => member.id)
);
// People who can be mentioned: members of private channels and DMs, everyone in public channels
const mentionable = computed(() => {
  const people =
    channel.value?.kind === 'public'
      ? agents.value
      : channel.value?.members || [];
  return people.filter(person => person.id !== currentUserId.value);
});

const scrollToEnd = () =>
  nextTick(() => {
    if (listRef.value) listRef.value.scrollTop = listRef.value.scrollHeight;
  });

const markRead = () => {
  const last = messages.value[messages.value.length - 1];
  if (last && channel.value?.joined)
    ChatAPI.read(channelId.value, last.id).catch(() => {});
};

const load = async () => {
  threadId.value = null;
  try {
    const [{ data: channelData }, { data: page }] = await Promise.all([
      ChatAPI.channel(channelId.value),
      ChatAPI.messages(channelId.value),
    ]);
    channel.value = channelData;
    chat.upsertChannel(channelData);
    messages.value = page.payload;
    hasMore.value = page.has_more;
    const lastRead = channelData.last_read_message_id;
    firstUnreadId.value =
      messages.value.find(
        message =>
          lastRead &&
          message.id > lastRead &&
          message.sender?.id !== currentUserId.value
      )?.id || null;
    chat.setActive(channelId.value);
    scrollToEnd();
    markRead();
  } catch (error) {
    useAlert(
      t('LOCO.CHAT.ERROR', {
        error: error.response?.data?.error || error.message,
      })
    );
  }
};

const loadOlder = async () => {
  if (!hasMore.value || isLoadingOlder.value || !messages.value.length) return;
  isLoadingOlder.value = true;
  const element = listRef.value;
  const previousHeight = element.scrollHeight;
  try {
    const { data } = await ChatAPI.messages(
      channelId.value,
      messages.value[0].id
    );
    messages.value = [...data.payload, ...messages.value];
    hasMore.value = data.has_more;
    nextTick(() => {
      element.scrollTop = element.scrollHeight - previousHeight;
    });
  } finally {
    isLoadingOlder.value = false;
  }
};

const onScroll = () => {
  if (listRef.value?.scrollTop < 80) loadOlder();
};

const isNearBottom = () => {
  const element = listRef.value;
  return (
    !element ||
    element.scrollHeight - element.scrollTop - element.clientHeight < 120
  );
};

const upsert = message => {
  if (message.channel_id !== channelId.value || message.parent_id) return;
  const index = messages.value.findIndex(item => item.id === message.id);
  if (index === -1) {
    const follow = isNearBottom() || message.sender?.id === currentUserId.value;
    messages.value.push(message);
    if (follow) scrollToEnd();
    if (!document.hidden) markRead();
  } else {
    messages.value.splice(index, 1, message);
  }
};

const onVisible = () => {
  if (!document.hidden) {
    chat.setActive(channelId.value);
    markRead();
  }
};

const join = async () => {
  const { data } = await ChatAPI.join(channelId.value);
  channel.value = data;
  chat.upsertChannel(data);
};

const leave = async () => {
  await ChatAPI.leave(channelId.value);
  await chat.reload();
  router.push({
    name: 'loco_team_chat',
    params: { accountId: route.params.accountId },
  });
};

const archive = async () => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('LOCO.CHAT.ARCHIVE_CONFIRM'))) return;
  await ChatAPI.archiveChannel(channelId.value);
  await chat.reload();
  router.push({
    name: 'loco_team_chat',
    params: { accountId: route.params.accountId },
  });
};

const setNotifications = async event => {
  const { data } = await ChatAPI.setNotifications(
    channelId.value,
    event.target.value
  );
  channel.value = data;
  chat.upsertChannel(data);
};

const addMembers = async () => {
  if (!newMembers.value.length) return;
  const { data } = await ChatAPI.addMembers(channelId.value, newMembers.value);
  channel.value = data;
  newMembers.value = [];
  membersDialogRef.value.close();
};

watch(channelId, id => {
  if (id) load();
});

onMounted(() => {
  load();
  emitter.on(CHAT_EVENTS.MESSAGE_CREATED, upsert);
  emitter.on(CHAT_EVENTS.MESSAGE_UPDATED, upsert);
  document.addEventListener('visibilitychange', onVisible);
});

onBeforeUnmount(() => {
  chat.setActive(null);
  emitter.off(CHAT_EVENTS.MESSAGE_CREATED, upsert);
  emitter.off(CHAT_EVENTS.MESSAGE_UPDATED, upsert);
  document.removeEventListener('visibilitychange', onVisible);
});
</script>

<template>
  <div class="flex w-full h-full min-h-0 bg-n-surface-1">
    <section class="flex flex-col flex-1 min-w-0 h-full">
      <header
        v-if="channel"
        class="flex items-center gap-3 px-4 h-14 flex-shrink-0 border-b border-n-weak"
      >
        <Icon
          :icon="
            channel.kind === 'direct'
              ? 'i-lucide-user'
              : channel.kind === 'private'
                ? 'i-lucide-lock'
                : 'i-lucide-hash'
          "
          class="size-4 text-n-slate-11"
        />
        <div class="flex flex-col min-w-0 flex-1">
          <span class="text-heading-3 text-n-slate-12 truncate">{{
            channel.name
          }}</span>
          <span
            v-if="channel.description"
            class="text-xs text-n-slate-11 truncate"
          >
            {{ channel.description }}
          </span>
        </div>
        <span class="text-xs text-n-slate-11">
          {{
            t(
              'LOCO.CHAT.MEMBERS',
              { count: channel.member_count },
              channel.member_count
            )
          }}
        </span>
        <template v-if="channel.joined">
          <select
            class="!mb-0 !w-auto !h-8 !py-0 text-xs"
            :value="channel.notifications"
            :aria-label="t('LOCO.CHAT.NOTIFICATIONS.LABEL')"
            @change="setNotifications"
          >
            <option
              v-for="level in ['all', 'mentions', 'none']"
              :key="level"
              :value="level"
            >
              {{ t(`LOCO.CHAT.NOTIFICATIONS.${level}`) }}
            </option>
          </select>
          <Button
            v-if="channel.kind !== 'direct'"
            v-tooltip.bottom="t('LOCO.CHAT.ADD_MEMBERS')"
            xs
            ghost
            slate
            icon="i-lucide-user-plus"
            @click="membersDialogRef.open()"
          />
          <Button
            v-if="channel.kind !== 'direct'"
            v-tooltip.bottom="t('LOCO.CHAT.LEAVE')"
            xs
            ghost
            slate
            icon="i-lucide-log-out"
            @click="leave"
          />
          <Button
            v-if="canManage && channel.kind !== 'direct'"
            v-tooltip.bottom="t('LOCO.CHAT.ARCHIVE')"
            xs
            ghost
            ruby
            icon="i-lucide-archive"
            @click="archive"
          />
        </template>
        <Button v-else xs :label="t('LOCO.CHAT.JOIN')" @click="join" />
      </header>

      <div
        ref="listRef"
        class="flex-1 overflow-y-auto py-2"
        @scroll.passive="onScroll"
      >
        <div v-if="hasMore" class="flex justify-center py-2">
          <Button
            xs
            faded
            slate
            :label="t('LOCO.CHAT.LOAD_OLDER')"
            :is-loading="isLoadingOlder"
            @click="loadOlder"
          />
        </div>
        <template v-for="(message, index) in messages" :key="message.id">
          <div
            v-if="
              index === 0 ||
              dayKey(messages[index - 1].created_at) !==
                dayKey(message.created_at)
            "
            class="flex items-center gap-3 px-4 my-3 text-xs font-medium text-n-slate-11"
          >
            <span class="flex-1 border-t border-n-weak" />
            {{ dayLabel(message.created_at, t, browserLocale) }}
            <span class="flex-1 border-t border-n-weak" />
          </div>
          <div
            v-if="message.id === firstUnreadId"
            class="flex items-center gap-3 px-4 my-2 text-xs font-medium text-n-ruby-11"
          >
            <span class="flex-1 border-t border-n-ruby-9" />
            {{ t('LOCO.CHAT.NEW_MESSAGES') }}
          </div>
          <ChatMessage
            :message="message"
            :people="
              mentionable.concat(
                currentUser
                  ? [
                      {
                        id: currentUser.id,
                        name: currentUser.available_name || currentUser.name,
                      },
                    ]
                  : []
              )
            "
            :current-user-id="currentUserId"
            :grouped="
              groupsWithPrevious(message, messages[index - 1]) &&
              message.id !== firstUnreadId
            "
            @open-thread="threadId = $event.id"
            @updated="upsert"
          />
        </template>
      </div>

      <p class="h-5 px-5 text-xs text-n-slate-11">
        <span v-if="typingNames.length">
          {{
            t(
              'LOCO.CHAT.TYPING',
              { names: typingNames.join(', ') },
              typingNames.length
            )
          }}
        </span>
      </p>
      <ChatComposer
        v-if="channel"
        ref="composerRef"
        :key="channel.id"
        :channel-id="channel.id"
        :people="mentionable"
        :initial-draft="channel.draft || ''"
        :placeholder="
          t('LOCO.CHAT.COMPOSER_PLACEHOLDER', {
            name: channel.kind === 'direct' ? channel.name : `#${channel.name}`,
          })
        "
        @sent="upsert"
      />
    </section>

    <ThreadPanel
      v-if="threadId && channel"
      :channel-id="channel.id"
      :message-id="threadId"
      :people="mentionable"
      :current-user-id="currentUserId"
      @close="threadId = null"
    />

    <Dialog
      ref="membersDialogRef"
      :title="t('LOCO.CHAT.ADD_MEMBERS')"
      :confirm-button-label="t('LOCO.CHAT.SAVE')"
      :cancel-button-label="t('LOCO.CHAT.CANCEL')"
      @confirm="addMembers"
    >
      <PeoplePicker
        v-model="newMembers"
        :people="agents"
        :exclude="memberIds"
        :placeholder="t('LOCO.CHAT.PICK_PEOPLE')"
      />
    </Dialog>
  </div>
</template>
